/**
 * Decision — show view on the shared Inspector grammar (design-system §5).
 *
 * The Decision is **the load-bearing human gate** (coverage §2: "a new mouth,
 * the same teeth"). When `status === "requested"` the Header surfaces the
 * approve / reject / cancel affordance: **Approve** is the single filled primary
 * action; Reject and Cancel are ghost actions beside it. Each transition is the
 * standard CRUD update (`PATCH /api/decisions/:id { status }`, `[EXISTS]`),
 * which the worker turns into the appropriate domain event (`decision.made` on
 * approval, `03 §10`). Relations link the gating Artifact / Project. Body is the
 * rationale markdown.
 *
 * `decision_type` is rendered as its **raw** value (e.g. `transition_proposal`)
 * so the proposal type stays machine-readable and stable for the
 * evidence-review e2e.
 */
import { useShow, useGo, useNavigation, type HttpError } from "@refinedev/core";
import { Box, FolderKanban, Pencil } from "@/components/ui/icon";
import type { Decision } from "@aos/shared";

import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { Markdown } from "@/components/app/MarkdownView";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  Field,
  FieldGrid,
  RefLink,
  RelationsBlock,
  AuditBlock,
  ShowPageSkeleton,
  ShowPageError,
  TechnicalId,
  type InspectorAction,
} from "@/components/inspector";
import { humanize } from "@/lib/format";
import { formatDateTime, orDash } from "../_shared/runs-ui";
import { useApiAction } from "../_shared/useApiAction";

export function DecisionShow() {
  const go = useGo();
  const { editUrl } = useNavigation();
  const { query } = useShow<Decision, HttpError>({ resource: "decisions" });
  const { data, isLoading, isError, error, refetch } = query;
  const { run, loading } = useApiAction();
  const record = data?.data;

  if (isLoading) return <ShowPageSkeleton />;
  if (isError || !record) {
    return (
      <ShowPageError
        message={error?.message ?? "Decision not found."}
        onRetry={() => void refetch()}
      />
    );
  }

  const setStatus = (status: Decision["status"], message: string) => {
    void run(`/decisions/${encodeURIComponent(record.id)}`, {
      method: "PATCH",
      body: { status },
      invalidateResources: ["decisions", "events"],
      successMessage: message,
    }).catch(() => {
      /* useApiAction surfaced the toast; degrade silently. */
    });
  };

  const isOpen = record.status === "requested";

  const menuActions: InspectorAction[] = [
    {
      label: "Edit",
      icon: Pencil,
      onClick: () => go({ to: editUrl("decisions", record.id) }),
    },
  ];

  return (
    <Inspector>
      <InspectorHeader
        title={record.title}
        subtitle="The human decision gate — rationale, approval state, and what it governs."
        status={record.status}
        primaryAction={
          isOpen ? (
            <Button
              disabled={loading}
              onClick={() => setStatus("approved", "Decision approved.")}
              data-testid="decision-approve"
            >
              Approve
            </Button>
          ) : undefined
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => go({ to: `/decisions/${record.id}/room` })}
            >
              <Icon name="decisions" data-icon="inline-start" aria-hidden />
              Decision Room
            </Button>
            {isOpen ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={loading}
                  onClick={() => setStatus("rejected", "Decision rejected.")}
                  data-testid="decision-reject"
                >
                  Reject
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={loading}
                  onClick={() => setStatus("cancelled", "Decision cancelled.")}
                  data-testid="decision-cancel"
                >
                  Cancel
                </Button>
              </>
            ) : null}
          </div>
        }
        menuActions={menuActions}
      />

      <InspectorSection title="Overview">
        <FieldGrid>
          {/* Raw decision_type (not humanized) — stable for the e2e + machine readable. */}
          <Field label="Decision type">{orDash(record.decision_type)}</Field>
          <Field label="Confidentiality">
            {humanize(record.confidentiality)}
          </Field>
          <Field label="Requested by">
            <RefLink resource="users" id={record.requested_by_user_id} />
          </Field>
          <Field label="Decided by">
            <RefLink resource="users" id={record.decided_by_user_id} />
          </Field>
          <Field label="Decided at">{formatDateTime(record.decided_at)}</Field>
          <Field label="Created">{formatDateTime(record.created_at)}</Field>
          <Field label="Updated">{formatDateTime(record.updated_at)}</Field>
          {record.description ? (
            <Field label="Description" full>
              <Markdown source={record.description} />
            </Field>
          ) : null}
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Rationale">
        {record.rationale ? (
          <Markdown source={record.rationale} />
        ) : (
          <p className="text-sm text-muted-foreground">No rationale recorded.</p>
        )}
      </InspectorSection>

      <InspectorSection title="Relations">
        <RelationsBlock
          groups={[
            {
              title: "Gates",
              items: [
                {
                  label: "Artifact",
                  icon: Box,
                  resource: "artifacts",
                  id: record.artifact_id,
                },
                {
                  label: "Project",
                  icon: FolderKanban,
                  resource: "projects",
                  id: record.project_id,
                },
              ],
            },
          ]}
        />
      </InspectorSection>

      <InspectorSection title="Technical">
        <FieldGrid className="text-muted-foreground">
          <Field label="Id">
            <TechnicalId id={record.id} />
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Audit">
        <AuditBlock entityType="decisions" entityId={record.id} />
      </InspectorSection>
    </Inspector>
  );
}

export default DecisionShow;
